import React, {useEffect, useMemo, useRef, useState} from 'react';
import {motion} from 'motion/react';
import {ArrowLeft, Check, Copy, Eye, EyeOff, Lock, Mail} from 'lucide-react';
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import {
    requestPasswordResetToken,
    resetPassword,
    verifyPasswordResetToken
} from "./LoginService";
import {getBackendWarningMessage} from "../../shared/utils";

export const PASSWORD_RESET_SCREENS = {
    FORGOT: 'forgot',
    TOKEN: 'token',
    RESET: 'reset',
    SUCCESS: 'success',
};

const supportMessage = 'Se o email não existir ou você não tiver acesso, entre em contato com o suporte técnico.';
const isValidEmail = (value = '') => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export default function PasswordResetFlow({
    primaryColor,
    accentColor,
    onBackToLogin,
    onError,
    onWarn,
    onInfo,
    onClearFeedback,
}) {
    const resetTimeoutRef = useRef(null);
    const [currentScreen, setCurrentScreen] = useState(PASSWORD_RESET_SCREENS.FORGOT);
    const [submitting, setSubmitting] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [recoveryToken, setRecoveryToken] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const tokenString = useMemo(() => recoveryToken.trim(), [recoveryToken]);

    useEffect(() => () => {
        if (resetTimeoutRef.current) {
            window.clearTimeout(resetTimeoutRef.current);
        }
    }, []);

    const resetFlow = () => {
        setResetEmail('');
        setRecoveryToken('');
        setNewPassword('');
        setConfirmPassword('');
        setCurrentScreen(PASSWORD_RESET_SCREENS.FORGOT);
    };

    const handleForgotPassword = async (event) => {
        event.preventDefault();
        onClearFeedback();

        if (!isValidEmail(resetEmail)) {
            onWarn('Informe um email válido.');
            return;
        }

        setSubmitting(true);
        try {
            await requestPasswordResetToken({email: resetEmail.trim()});
            onInfo(`Enviamos um código de 6 dígitos para ${resetEmail.trim()}.`);
            setRecoveryToken('');
            setCurrentScreen(PASSWORD_RESET_SCREENS.TOKEN);
        } catch (err) {
            const backendMessage = getBackendWarningMessage(err);
            if (err?.response?.status === 404) {
                onWarn(`Email não encontrado. ${supportMessage}`);
            } else if (backendMessage) {
                onWarn(backendMessage);
            } else {
                onError(`Não foi possível enviar o código. ${supportMessage}`);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleRecoveryTokenChange = (value) => {
        setRecoveryToken(value.trim());
    };

    const handlePasteFromClipboard = async () => {
        try {
            const text = await navigator.clipboard.readText();
            handleRecoveryTokenChange(text);
        } catch {
            onWarn('Não foi possível ler o token da área de transferência.');
        }
    };

    const handleTokenSubmit = async (event) => {
        event.preventDefault();
        onClearFeedback();

        if (tokenString.length < 6) {
            onWarn('Informe o token de segurança completo.');
            return;
        }

        setSubmitting(true);
        try {
            await verifyPasswordResetToken({
                email: resetEmail.trim(),
                token: tokenString,
            });
            setCurrentScreen(PASSWORD_RESET_SCREENS.RESET);
        } catch (err) {
            const backendMessage = getBackendWarningMessage(err);
            onWarn(backendMessage || 'Token inválido ou expirado. Solicite um novo código.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleResendToken = async () => {
        onClearFeedback();
        setSubmitting(true);

        try {
            await requestPasswordResetToken({email: resetEmail.trim()});
            setRecoveryToken('');
            onInfo(`Reenviamos um novo código para ${resetEmail.trim()}.`);
        } catch (err) {
            const backendMessage = getBackendWarningMessage(err);
            onWarn(backendMessage || `Não foi possível reenviar o código. ${supportMessage}`);
        } finally {
            setSubmitting(false);
        }
    };

    const handlePasswordReset = async (event) => {
        event.preventDefault();
        onClearFeedback();

        if (newPassword.length < 6) {
            onWarn('A senha deve ter pelo menos 6 caracteres.');
            return;
        }

        if (newPassword !== confirmPassword) {
            onWarn('As senhas não coincidem.');
            return;
        }

        setSubmitting(true);
        try {
            await resetPassword({
                email: resetEmail.trim(),
                token: tokenString,
                password: newPassword,
            });
            setCurrentScreen(PASSWORD_RESET_SCREENS.SUCCESS);
            onInfo('Senha redefinida com sucesso.');

            resetTimeoutRef.current = window.setTimeout(() => {
                resetFlow();
                onClearFeedback();
                onBackToLogin();
            }, 3000);
        } catch (err) {
            const backendMessage = getBackendWarningMessage(err);
            onWarn(backendMessage || 'Não foi possível redefinir a senha. Gere um novo código e tente novamente.');
        } finally {
            setSubmitting(false);
        }
    };

    const renderSectionHeader = (title, description, previousScreen = null) => (
        <>
            <Box sx={{width: '100%', display: 'flex', alignItems: 'center', mb: 3}}>
                <IconButton
                    onClick={() => {
                        onClearFeedback();
                        if (previousScreen) {
                            setCurrentScreen(previousScreen);
                        } else {
                            onBackToLogin();
                        }
                    }}
                    sx={{mr: 1}}
                >
                    <ArrowLeft size={20}/>
                </IconButton>
                <Typography variant="h5" component="h1" sx={{fontWeight: 700}}>
                    {title}
                </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{mb: 4, textAlign: 'center'}}>
                {description}
            </Typography>
        </>
    );

    if (currentScreen === PASSWORD_RESET_SCREENS.SUCCESS) {
        return (
            <>
                <Box sx={{display: 'flex', justifyContent: 'center', mb: 3}}>
                    <motion.div
                        initial={{scale: 0}}
                        animate={{scale: 1}}
                        transition={{type: "spring", stiffness: 200, damping: 15}}
                        style={{
                            height: 80,
                            width: 80,
                            borderRadius: '50%',
                            background: `linear-gradient(to right, ${primaryColor}, ${accentColor})`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Check size={48} color="white" strokeWidth={3}/>
                    </motion.div>
                </Box>

                <Typography variant="h5" component="h1" sx={{fontWeight: 700, mb: 2, textAlign: 'center'}}>
                    Senha Redefinida
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{mb: 4, textAlign: 'center'}}>
                    Sua senha foi alterada com sucesso. Você será redirecionado para o login em instantes.
                </Typography>

                <motion.div initial={{opacity: 0}} animate={{opacity: 1}} transition={{delay: 0.5}}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            resetFlow();
                            onBackToLogin();
                        }}
                        sx={{py: 1.5, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'}}
                    >
                        Ir para Login
                    </Button>
                </motion.div>
            </>
        );
    }

    if (currentScreen === PASSWORD_RESET_SCREENS.RESET) {
        return (
            <>
                {renderSectionHeader('Nova Senha', 'Crie uma nova senha segura para sua conta.', PASSWORD_RESET_SCREENS.TOKEN)}

                <Box component="form" onSubmit={handlePasswordReset} sx={{width: '100%'}}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        label="Nova Senha"
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(event) => setNewPassword(event.target.value)}
                        placeholder="••••••••"
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Lock size={20}/></InputAdornment>,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowNewPassword((current) => !current)} edge="end">
                                        {showNewPassword ? <EyeOff size={20}/> : <Eye size={20}/>}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        label="Confirmar Nova Senha"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                        placeholder="••••••••"
                        error={confirmPassword !== '' && newPassword !== confirmPassword}
                        helperText={confirmPassword !== '' && newPassword !== confirmPassword ? 'As senhas não coincidem' : ''}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Lock size={20}/></InputAdornment>,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowConfirmPassword((current) => !current)} edge="end">
                                        {showConfirmPassword ? <EyeOff size={20}/> : <Eye size={20}/>}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <motion.div whileHover={{scale: 1.02}} whileTap={{scale: 0.98}}>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            disabled={submitting}
                            sx={{mt: 3, mb: 2, py: 1.5, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'}}
                        >
                            {submitting ? 'Redefinindo...' : 'Redefinir Senha'}
                        </Button>
                    </motion.div>
                </Box>
            </>
        );
    }

    if (currentScreen === PASSWORD_RESET_SCREENS.TOKEN) {
        return (
            <>
                {renderSectionHeader('Token de Segurança', 'Digite ou cole o token enviado para o seu email cadastrado.', PASSWORD_RESET_SCREENS.FORGOT)}

                <Typography variant="body2" sx={{mb: 4, textAlign: 'center', fontWeight: 600, color: primaryColor}}>
                    {resetEmail}
                </Typography>

                <Box component="form" onSubmit={handleTokenSubmit} sx={{width: '100%'}}>
                    <TextField
                        fullWidth
                        autoFocus
                        label="Token de Segurança"
                        value={recoveryToken}
                        onChange={(event) => handleRecoveryTokenChange(event.target.value)}
                        placeholder="Cole ou digite o token"
                        helperText={recoveryToken ? `${recoveryToken.length} caracteres` : 'Mínimo 6 caracteres'}
                        inputProps={{
                            style: {
                                fontFamily: 'monospace',
                                fontSize: '14px',
                                letterSpacing: '0.5px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }
                        }}
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                minHeight: 56,
                                '&.Mui-focused fieldset': {
                                    borderColor: primaryColor,
                                    borderWidth: 2,
                                },
                            },
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="colar token"
                                        onClick={handlePasteFromClipboard}
                                        edge="end"
                                        title="Colar da área de transferência"
                                    >
                                        <Copy size={20}/>
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Box
                        sx={{
                            mb: 3,
                            p: 2,
                            bgcolor: 'grey.100',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.300',
                        }}
                    >
                        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 1}}>
                            Dica: cole o token diretamente do email recebido.
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Verifique sua caixa de entrada, spam ou lixo eletrônico.
                        </Typography>
                    </Box>

                    <motion.div whileHover={{scale: 1.02}} whileTap={{scale: 0.98}}>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            disabled={submitting || tokenString.length < 6}
                            sx={{mb: 2, py: 1.5, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'}}
                        >
                            {submitting ? 'Verificando...' : 'Verificar Código'}
                        </Button>
                    </motion.div>

                    <Button fullWidth variant="text" onClick={handleResendToken} disabled={submitting}>
                        Reenviar Código
                    </Button>
                </Box>
            </>
        );
    }

    return (
        <>
            {renderSectionHeader(
                'Recuperar Senha',
                'Informe o email cadastrado para receber um código de verificação. Se você não tiver acesso, entre em contato com o suporte técnico.'
            )}

            <Box component="form" onSubmit={handleForgotPassword} sx={{width: '100%'}}>
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    label="Email cadastrado"
                    autoComplete="email"
                    autoFocus
                    value={resetEmail}
                    onChange={(event) => setResetEmail(event.target.value)}
                    placeholder="seu@email.com"
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><Mail size={20}/></InputAdornment>,
                    }}
                />

                <motion.div whileHover={{scale: 1.02}} whileTap={{scale: 0.98}}>
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        disabled={submitting}
                        sx={{mt: 3, mb: 2, py: 1.5, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'}}
                    >
                        {submitting ? 'Enviando código...' : 'Enviar Código'}
                    </Button>
                </motion.div>

                <Button fullWidth variant="text" onClick={onBackToLogin}>
                    Voltar ao Login
                </Button>
            </Box>
        </>
    );
}
